import { useEffect, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";

// Session Z33 — Focus Mode Overlay
// When Inbox auto-snoozes because a Google Calendar meeting just started,
// it writes `ns_focus_mode = {end_iso, summary}` to localStorage. This
// component reads that flag app-wide and paints a subtle dim + top-right
// "Meeting mode" chip so it's obvious at a glance the app is silenced.
//
// The overlay is `pointer-events-none` so the app stays fully usable — this
// is a visual signal, not a modal.
export default function FocusModeOverlay() {
  const [focus, setFocus] = useState(null);

  const read = () => {
    try {
      const raw = localStorage.getItem("ns_focus_mode");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.end_iso) return null;
      const end = new Date(parsed.end_iso).getTime();
      if (!end || end <= Date.now()) {
        localStorage.removeItem("ns_focus_mode");
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    setFocus(read());
    const tick = setInterval(() => setFocus(read()), 15000);
    const onStorage = (e) => {
      if (e.key === "ns_focus_mode" || e.key === "ns_snooze_until") setFocus(read());
    };
    const onCustom = () => setFocus(read());
    window.addEventListener("storage", onStorage);
    window.addEventListener("ns:focus-mode", onCustom);
    return () => {
      clearInterval(tick);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("ns:focus-mode", onCustom);
    };
  }, []);

  if (!focus) return null;

  const endsAt = new Date(focus.end_iso);
  const endsPretty = endsAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  return (
    <div
      data-testid="focus-mode-overlay"
      className="fixed inset-0 z-40 pointer-events-none"
      aria-hidden="true"
    >
      {/* Warm dim wash — never fully opaque so the app remains legible. */}
      <div className="absolute inset-0 bg-slate-950/[0.08] backdrop-brightness-95" />
      {/* Subtle inner glow at the edges to feel like a spotlight. */}
      <div className="absolute inset-0 shadow-[inset_0_0_120px_rgba(15,23,42,0.18)]" />
      {/* Persistent status chip — bottom-right so it doesn't block search bars. */}
      <div
        data-testid="focus-mode-chip"
        className="absolute bottom-6 right-6 pointer-events-auto"
      >
        <div className="flex items-center gap-2 rounded-full bg-slate-900/95 text-white pl-3 pr-4 py-2 shadow-lg border border-slate-800">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-400" />
          </span>
          <CalendarIcon className="w-3.5 h-3.5 text-indigo-300" />
          <div className="text-[11px] leading-tight">
            <div className="font-medium">Meeting mode</div>
            <div className="text-[10px] text-slate-300 max-w-[220px] truncate">
              {focus.summary || "In a meeting"} · silent until {endsPretty}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
